/**
 * AgentX Manager — design tokens.
 *
 * Single source of truth for colour, radius, elevation and type. Components must
 * read colour from the MUI theme (semantic tokens) rather than importing raw hex
 * from here; the raw scales exist so the theme itself can be assembled, plus a
 * few places that sit outside MUI's palette (charts, the brand mark).
 */

/** Brand ramp — purple, tuned so 500 passes 4.5:1 on white (#705596 ≈ 5.9:1). */
export const brand = {
  50: '#F2EEF8',
  100: '#E4DCF0',
  200: '#CBBCE1',
  300: '#AD97CE',
  400: '#8B6FC0',
  500: '#705596',
  600: '#62498A',
  700: '#523C73',
  800: '#43315F',
  900: '#362750',
} as const;

/**
 * Neutral ramp — the palette's greys. Neutral rather than cooled: the brand
 * purple supplies the warmth, and 100/300 carry a faint purple tint so sidebars
 * and dividers sit under the brand without turning blue.
 */
export const slate = {
  25: '#FAFAFA',
  50: '#F5F5F5',
  100: '#EDEDF5',
  200: '#DFDFDF',
  300: '#C4B7D6',
  400: '#8B8B8B',
  500: '#616161',
  600: '#4A4A4A',
  700: '#3B3B3B',
  800: '#2B2B2B',
  900: '#1F1F1F',
  950: '#121212',
} as const;

/** Status hues. Light/dark variants are picked per colour scheme in the theme. */
export const status = {
  success: { light: '#2F7A2B', main: '#388A34', dark: '#286F25', onDark: '#73C991' },
  warning: { light: '#A87603', main: '#BF8803', dark: '#8E6502', onDark: '#CCA700' },
  error: { light: '#E51400', main: '#E51400', dark: '#B81000', onDark: '#F85149' },
  info: { light: '#005FB8', main: '#005FB8', dark: '#004C93', onDark: '#3794FF' },
} as const;

/**
 * Categorical chart palette. Ordered by perceptual distance so the first N
 * series stay distinguishable; each entry also carries a dash pattern so series
 * remain readable without colour (WCAG: don't encode by colour alone).
 */
export const chartSeries = [
  { color: '#705596', dash: undefined },
  { color: '#388A34', dash: '6 3' },
  { color: '#BF8803', dash: '2 3' },
  { color: '#005FB8', dash: '10 4' },
  { color: '#AD97CE', dash: '4 2 1 2' },
  { color: '#E51400', dash: '8 3 2 3' },
] as const;

export const chartColors = chartSeries.map((s) => s.color);

/** Matches the palette's --r-sm / --r-md / --r-lg / --r-pill. */
export const radius = {
  xs: 6,
  sm: 8,
  md: 12,
  lg: 16,
  xl: 18,
  pill: 999,
} as const;

export const fonts = {
  sans: '"Inter", "Segoe UI", system-ui, -apple-system, "Helvetica Neue", Arial, sans-serif',
  mono: '"JetBrains Mono", "Fira Code", ui-monospace, SFMono-Regular, Menlo, Consolas, monospace',
} as const;

export const layout = {
  sidebarWidth: 264,
  sidebarRailWidth: 76,
  topbarHeight: 64,
} as const;

/**
 * Flat by design: surfaces separate with a border, not a shadow. Only floating
 * layers — dialogs, menus, popovers, toasts — cast one, so the low steps are
 * deliberately `none` and the scale tops out at the palette's single --shadow.
 */
export const shadowScale = (mode: 'light' | 'dark') =>
  mode === 'light'
    ? [
        'none',
        'none',
        'none',
        '0 4px 14px rgba(0,0,0,0.08)',
        '0 4px 14px rgba(0,0,0,0.08)',
        '0 4px 14px rgba(0,0,0,0.08)',
      ]
    : [
        'none',
        'none',
        'none',
        '0 4px 14px rgba(0,0,0,0.32)',
        '0 4px 14px rgba(0,0,0,0.32)',
        '0 4px 14px rgba(0,0,0,0.32)',
      ];
